/**
 * Holds the BrickLink quota warning the API ships on every response.
 *
 * The API attaches `bricklink_api_metadata.quota_warning` to every response
 * body once the user reaches their daily BrickLink quota. The quota is a soft
 * limit, so nothing is blocked — the warning is the only signal the user gets,
 * and it must reach the header without every service hook threading it through.
 *
 * This is an external store rather than app state because `processResponse` in
 * hooks/services/api.js is the single funnel every response passes through, and
 * that funnel sits in the service layer. Dispatching to AppStateProvider from
 * there would invert the six-layer flow. Components subscribe with
 * `useSyncExternalStore`.
 */

let currentQuota = null;
const listeners = new Set();

const notify = () => {
    listeners.forEach((listener) => listener());
};

const isSameQuota = (a, b) => {
    if (a === b) {
        return true;
    }
    if (a === null || b === null) {
        return false;
    }
    return a.current_count === b.current_count
        && a.daily_limit === b.daily_limit
        && a.warning?.code === b.warning?.code
        && a.warning?.message === b.warning?.message;
};

/**
 * Record the quota warning carried by an API response.
 *
 * A response whose body has no `bricklink_api_metadata` carries no quota news —
 * the API omits the block entirely when the user's data source is neither `api`
 * nor `scraper`. Those responses leave the last known warning in place rather
 * than clearing it, because they are silent, not authoritative.
 *
 * @param {Object|null|undefined} metadata - The response's bricklink_api_metadata
 */
export const recordBricklinkApiMetadata = (metadata) => {
    if (!metadata) {
        return;
    }

    const next = {
        current_count: metadata.current_count,
        daily_limit: metadata.daily_limit,
        warning: metadata.quota_warning === undefined ? null : metadata.quota_warning
    };

    if (isSameQuota(next, currentQuota)) {
        return;
    }

    currentQuota = next;
    notify();
};

/**
 * Seed the store from the user profile the header fetches on mount, so the
 * counter renders before the first API response lands. Responses overwrite this
 * with the fresher server-side snapshot; the store stays the only source the
 * counter reads.
 *
 * @param {{current_count: number, daily_limit: number}} usage
 */
export const seedBricklinkQuotaFromUser = (usage) => {
    const next = {
        current_count: usage.current_count,
        daily_limit: usage.daily_limit,
        warning: currentQuota === null ? null : currentQuota.warning
    };

    if (isSameQuota(next, currentQuota)) {
        return;
    }

    currentQuota = next;
    notify();
};

/**
 * Subscribe to quota changes. Returns the unsubscribe function.
 * @param {Function} listener
 * @returns {Function}
 */
export const subscribeToBricklinkQuota = (listener) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};

/**
 * @returns {{current_count: number, daily_limit: number, warning: Object|null}|null}
 */
export const getBricklinkQuota = () => currentQuota;

/** Test seam. Clears the recorded quota and every subscriber. */
export const resetBricklinkQuotaWarning = () => {
    currentQuota = null;
    listeners.clear();
};
